import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { useAuth } from '../hooks/useAuth';
import { getStoredTokens, socialApi } from '../services/api';
import { useToast } from './ToastContext';
import type { AppNotification, RealtimeEvent } from '../types';
import { UserPlus, Check, Film, MessageCircle } from 'lucide-react';
import { createElement } from 'react';
import { playNotificationChime } from '../utils/notificationSound';

interface RealtimeContextValue {
  notifications: AppNotification[];
  unreadNotifications: number;
  unreadMessages: number;
  totalUnread: number;
  connected: boolean;
  refresh: () => Promise<void>;
  markAllRead: () => Promise<void>;
  subscribe: (cb: (e: RealtimeEvent) => void) => () => void;
  setActiveThreadUser: (userId: string | null) => void;
  send: (data: unknown) => void;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export function useRealtime(): RealtimeContextValue {
  const ctx = useContext(RealtimeContext);
  if (!ctx) throw new Error('useRealtime must be used within a RealtimeProvider');
  return ctx;
}

function notificationToToast(n: AppNotification) {
  const fromName = n.data?.user?.name || n.data?.fromUser?.name || 'Someone';
  switch (n.type) {
    case 'friend_request':
      return {
        title: 'New friend request',
        description: `${fromName} wants to connect`,
        icon: createElement(UserPlus, { className: 'h-5 w-5 text-primary-500' }),
        href: '/friends',
      };
    case 'friend_accept':
      return {
        title: 'Friend request accepted',
        description: `${fromName} is now your friend`,
        icon: createElement(Check, { className: 'h-5 w-5 text-green-500' }),
        href: '/friends',
      };
    case 'movie':
      return {
        title: `${fromName} sent you a movie`,
        description: n.data?.movie?.title || 'Tap to view',
        icon: createElement(Film, { className: 'h-5 w-5 text-primary-500' }),
        href: '/messages',
      };
    default:
      return {
        title: 'New notification',
        icon: createElement(MessageCircle, { className: 'h-5 w-5 text-primary-500' }),
        href: '/messages',
      };
  }
}

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const { showToast } = useToast();

  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [connected, setConnected] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<number | null>(null);
  const pingRef = useRef<number | null>(null);
  const subscribersRef = useRef<Set<(e: RealtimeEvent) => void>>(new Set());
  const activeThreadUserRef = useRef<string | null>(null);
  const attemptRef = useRef(0);

  const unreadNotifications = notifications.filter((n) => !n.isRead).length;
  const totalUnread = unreadNotifications + unreadMessages;

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await socialApi.listNotifications();
      setNotifications(data.notifications);
      setUnreadMessages(data.unreadMessages);
    } catch {
      // ignore transient failures
    }
  }, [isAuthenticated]);

  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    try {
      await socialApi.markNotificationsRead();
    } catch {
      // ignore
    }
  }, []);

  const subscribe = useCallback((cb: (e: RealtimeEvent) => void) => {
    subscribersRef.current.add(cb);
    return () => {
      subscribersRef.current.delete(cb);
    };
  }, []);

  const setActiveThreadUser = useCallback((userId: string | null) => {
    activeThreadUserRef.current = userId;
  }, []);

  const send = useCallback((data: unknown) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify(data));
    } catch {
      // ignore
    }
  }, []);

  const handleEvent = useCallback(
    (event: RealtimeEvent) => {
      // Fan out to subscribers (e.g. the open conversation)
      subscribersRef.current.forEach((cb) => {
        try {
          cb(event);
        } catch {
          // ignore subscriber errors
        }
      });

      if (event.kind === 'notification') {
        setNotifications((prev) => {
          if (prev.some((n) => n.id === event.notification.id)) return prev;
          return [event.notification, ...prev];
        });
        // In-app chime: OS push is often silent when this tab is focused
        playNotificationChime();
        const t = notificationToToast(event.notification);
        showToast({
          title: t.title,
          description: t.description,
          icon: t.icon,
          onClick: () => {
            window.location.href = t.href;
          },
        });
      } else if (event.kind === 'message') {
        const msg = event.message;
        const isIncoming = msg.recipientId === user?.id;
        const isActive = activeThreadUserRef.current === msg.senderId;
        if (isIncoming && !isActive) {
          setUnreadMessages((c) => c + 1);
          playNotificationChime();
          showToast({
            title: event.from?.name || 'New message',
            description: msg.type === 'movie' ? `Sent a movie: ${msg.movie?.title ?? ''}` : msg.content ?? '',
            icon: createElement(MessageCircle, { className: 'h-5 w-5 text-primary-500' }),
            onClick: () => {
              window.location.href = '/messages';
            },
          });
        }
      }
    },
    [showToast, user?.id]
  );

  // Manage the WebSocket connection lifecycle
  useEffect(() => {
    if (!isAuthenticated) {
      setConnected(false);
      return;
    }

    let closed = false;

    const connect = () => {
      const tokens = getStoredTokens();
      if (!tokens?.accessToken) return;

      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const url = `${proto}//${window.location.host}/api/ws?token=${encodeURIComponent(
        tokens.accessToken
      )}`;

      let ws: WebSocket;
      try {
        ws = new WebSocket(url);
      } catch {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        attemptRef.current = 0;
        setConnected(true);
        if (pingRef.current) window.clearInterval(pingRef.current);
        pingRef.current = window.setInterval(() => {
          try {
            ws.send('ping');
          } catch {
            // ignore
          }
        }, 25000);
      };

      ws.onmessage = (e) => {
        if (e.data === 'pong') return;
        try {
          const parsed = JSON.parse(e.data) as RealtimeEvent;
          handleEvent(parsed);
        } catch {
          // ignore malformed
        }
      };

      ws.onclose = () => {
        setConnected(false);
        if (pingRef.current) {
          window.clearInterval(pingRef.current);
          pingRef.current = null;
        }
        if (!closed) scheduleReconnect();
      };

      ws.onerror = () => {
        try {
          ws.close();
        } catch {
          // ignore
        }
      };
    };

    const scheduleReconnect = () => {
      if (closed) return;
      attemptRef.current += 1;
      const delay = Math.min(30000, 1000 * 2 ** Math.min(attemptRef.current, 5));
      if (reconnectRef.current) window.clearTimeout(reconnectRef.current);
      reconnectRef.current = window.setTimeout(connect, delay);
    };

    refresh();
    connect();

    return () => {
      closed = true;
      if (reconnectRef.current) window.clearTimeout(reconnectRef.current);
      if (pingRef.current) window.clearInterval(pingRef.current);
      try {
        wsRef.current?.close();
      } catch {
        // ignore
      }
      wsRef.current = null;
    };
  }, [isAuthenticated, handleEvent, refresh]);

  return (
    <RealtimeContext.Provider
      value={{
        notifications,
        unreadNotifications,
        unreadMessages,
        totalUnread,
        connected,
        refresh,
        markAllRead,
        subscribe,
        setActiveThreadUser,
        send,
      }}
    >
      {children}
    </RealtimeContext.Provider>
  );
}
